import { ListingMatch } from '@deltek/specification-client/core/Models/listingSearch'
import { SpecColumn } from '@deltek/specification-client/core/Widgets/SpecTable'

export const defaultColumnKeys = [
    'displayNameWithFirmName',
    'categories',
    'familyNumber',
    'sectionName',
    'productType',
    'model',
    'productName',
    'type',
    'isActive',
    'submissionStatus',
]

export const availableColumnKeys = [
    'firmDisplayName',
    'firmName',
    'hasManufacturerOptions',
    'lastUpdated',
    'specAgentProductTypeId',
    'partnershipLinksSwatchbox',
    'partnershipLinksSM',
    'partnershipLinksULE',
    'contentLibrary',
]

export const deltekOrResellerColumnKeys = [
    'canAutoGenerateProductSpecification',
    'partnershipLinksBIM',
    'updatedLastAiScrapeDate',
    'fullLengthDoc',
    'description',
    'assignedContactUsers',
    'assignedBPMUsers',
    'assignedDeltekUsers',
]

const adminOnlyColumnKeys = [
    'hasProductDetailNotes',
    'listingId',
    'firmId',
    'firmLogoUrl',
    'importCatalog',
]

const productHistoryColumnKeys = [
    'timeStamp',
    'user',
    'type',
    'submissionStatus',
    'isActive',
    'reason',
    'note',
]

export type ColumnMapType = ListingMatch & {
    user: string
    timeStamp: string
    reason: string
    note: boolean
    [key: string]: any // this allows for extension
}

export const columnMap: Record<
    string,
    SpecColumn<ColumnMapType> & {
        isGrouped: boolean
        canExpand: boolean
        formattedText?: (value: any) => string
    }
> = {
    displayNameWithFirmName: {
        id: 'displayNameWithFirmName',
        accessor: 'displayNameWithFirmName',
        Header: 'Company Full Name',
        isGrouped: false,
        canExpand: true,
    },
    categories: {
        id: 'categories',
        accessor: 'categories',
        Header: 'Category',
        isGrouped: false,
        canExpand: true,
    },
    familyNumber: {
        id: 'familyNumber',
        accessor: 'familyNumber',
        Header: 'Family Number',
        isGrouped: false,
        canExpand: true,
        minWidth: 120,
    },
    sectionName: {
        id: 'sectionName',
        accessor: 'sectionName',
        Header: 'Family Name',
        isGrouped: false,
        canExpand: true,
    },
    productType: {
        id: 'productType',
        accessor: 'productType',
        Header: 'Product Type Name',
        isGrouped: false,
        canExpand: true,
    },
    model: {
        id: 'model',
        accessor: 'model',
        Header: 'Product Model Number',
        isGrouped: false,
        canExpand: true,
    },
    productName: {
        id: 'productName',
        accessor: 'productName',
        Header: 'Product Name',
        isGrouped: false,
        canExpand: true,
    },
    type: {
        id: 'type',
        accessor: 'type',
        Header: 'Listing Type',
        isGrouped: false,
        canExpand: true,
    },

    hasManufacturerOptions: {
        id: 'hasManufacturerOptions',
        accessor: 'hasManufacturerOptions',
        Header: 'Has Manufacturer Options',
        isGrouped: false,
        canExpand: true,
    },
    isActive: {
        id: 'isActive',
        accessor: 'isActive',
        Header: 'Status',
        isGrouped: false,
        canExpand: true,
        sortDescFirst: true,
        minWidth: 120,
    },
    submissionStatus: {
        id: 'submissionStatus',
        accessor: 'submissionStatus',
        Header: 'State',
        isGrouped: false,
        canExpand: true,
    },
    listingId: {
        id: 'listingId',
        accessor: 'listingId',
        Header: 'Listing ID',
        isGrouped: false,
        canExpand: true,
    },
    firmDisplayName: {
        id: 'firmDisplayName',
        accessor: 'firmDisplayName',
        Header: 'Company Display Name',
        isGrouped: false,
        canExpand: true,
    },
    firmId: {
        id: 'firmId',
        accessor: 'firmId',
        Header: 'Company ID',
        isGrouped: false,
        canExpand: true,
    },
    firmLogoUrl: {
        id: 'firmLogoUrl',
        accessor: 'firmLogoUrl',
        Header: 'Company Logo URL',
        isGrouped: false,
        canExpand: true,
    },
    firmName: {
        id: 'firmName',
        accessor: 'firmName',
        Header: 'Company Name',
        isGrouped: false,
        canExpand: true,
    },
    lastUpdated: {
        id: 'lastUpdated',
        accessor: 'lastUpdated',
        Header: 'Last Updated',
        isGrouped: false,
        canExpand: true,
    },
    canAutoGenerateProductSpecification: {
        id: 'canAutoGenerateProductSpecification',
        accessor: 'canAutoGenerateProductSpecification',
        Header: 'Auto Generate',
        isGrouped: false,
        canExpand: true,
        sortDescFirst: true,
    },
    specAgentProductTypeId: {
        id: 'specAgentProductTypeId',
        accessor: 'specAgentProductTypeId',
        Header: 'SA Product Type ID',
        isGrouped: false,
        canExpand: true,
    },
    partnershipLinksSwatchbox: {
        id: 'partnershipLinksSwatchbox',
        accessor: 'partnershipLinksSwatchbox',
        Header: 'Swatchbox',
        isGrouped: false,
        canExpand: true,
    },
    partnershipLinksSM: {
        id: 'partnershipLinksSM',
        accessor: 'partnershipLinksSM',
        Header: 'Sustainable Minds',
        isGrouped: false,
        canExpand: true,
    },
    partnershipLinksULE: {
        id: 'partnershipLinksULE',
        accessor: 'partnershipLinksULE',
        Header: 'ULE',
        isGrouped: false,
        canExpand: true,
    },
    partnershipLinksBIM: {
        id: 'partnershipLinksBIM',
        accessor: 'partnershipLinksBIM',
        Header: 'BIMsmith',
        isGrouped: false,
        canExpand: true,
    },
    fullLengthDoc: {
        id: 'fullLengthDoc',
        accessor: 'fullLengthDoc',
        Header: 'Full Length Doc',
        isGrouped: false,
        canExpand: true,
    },
    importCatalog: {
        id: 'importCatalog',
        accessor: 'importCatalog',
        Header: 'Import Catalog',
        isGrouped: false,
        canExpand: true,
    },
    description: {
        id: 'description',
        accessor: 'description',
        Header: 'Description',
        isGrouped: false,
        canExpand: true,
    },
    assignedContactUsers: {
        id: 'assignedContactUsers',
        accessor: 'assignedContactUsers',
        Header: 'Product Contact',
        isGrouped: false,
        canExpand: true,
    },
    assignedBPMUsers: {
        id: 'assignedBPMUsers',
        accessor: 'assignedBPMUsers',
        Header: 'Assigned BPM',
        isGrouped: false,
        canExpand: true,
    },
    assignedDeltekUsers: {
        id: 'assignedDeltekUsers',
        accessor: 'assignedDeltekUsers',
        Header: 'Assigned Deltek',
        isGrouped: false,
        canExpand: true,
    },
    updatedLastAiScrapeDate: {
        id: 'updatedLastAiScrapeDate',
        accessor: 'updatedLastAiScrapeDate',
        Header: 'Last Scraped',
        isGrouped: false,
        canExpand: true,
    },
    hasProductDetailNotes: {
        id: 'hasProductDetailNotes',
        accessor: 'hasProductDetailNotes',
        Header: 'Note',
        isGrouped: false,
        canExpand: true,
        minWidth: 120,
    },
    contentLibrary: {
        id: 'contentLibrary',
        accessor: 'contentLibrary',
        Header: 'Product Family',
        isGrouped: false,
        canExpand: true,
    },
}

// Non-admin baseline: availableColumnKeys only — used by ProductsPage and SpecTable store
export const baseColumns = availableColumnKeys.map(
    (key) => columnMap[key]
) as SpecColumn<ListingMatch>[]

// Deltek/reseller baseline: includes deltekOrResellerColumnKeys — used via getColumnMap
export const columns = [
    ...availableColumnKeys,
    ...deltekOrResellerColumnKeys,
].map((key) => columnMap[key]) as SpecColumn<ListingMatch>[]

export const adminColumnKeys = [
    ...availableColumnKeys,
    ...deltekOrResellerColumnKeys,
    ...adminOnlyColumnKeys,
]

export const adminColumns = adminColumnKeys.map(
    (key) => columnMap[key]
) as SpecColumn<ListingMatch>[]

export const getColumnMap = (
    isAdminAccess: boolean,
    isDeltekOrReseller = false
): SpecColumn<ListingMatch>[] => {
    if (isAdminAccess) return adminColumns
    if (isDeltekOrReseller) return columns
    return baseColumns
}

export const defaultColumns = defaultColumnKeys.map(
    (key) => columnMap[key]
) as SpecColumn<ListingMatch>[]

export const productHistoryColumns = productHistoryColumnKeys.map((key) => {
    return {
        ...columnMap,
        ...{
            timeStamp: {
                id: 'timeStamp',
                accessor: 'timeStamp',
                Header: 'Date',
                isGrouped: false,
                canExpand: false,
            },
            user: {
                id: 'user',
                accessor: 'user',
                Header: 'User',
                isGrouped: false,
                canExpand: false,
            },
            reason: {
                id: 'reason',
                accessor: 'reason',
                Header: 'Note Reason',
                isGrouped: false,
                canExpand: false,
            },
            note: {
                id: 'note',
                accessor: 'note',
                Header: 'Note',
                isGrouped: false,
                canExpand: false,
            },
        },
    }[key]
}) as SpecColumn<ColumnMapType>[]
